// El modo aula: la puerta por la que entra un alumno SIN CUENTA y las tres del profesor.
//
// POR QUE UN FICHERO PROPIO y no un apartado de `communities.js`. La sesión de aula cuelga de
// una clase, sí, pero es lo único de toda la casa que se sirve sin sesión y con una llave que
// no es la sesión. Tenerlo entero en un sitio —cómo se canjea el código, por dónde viaja el
// token y dónde se guarda— es lo que hace que se pueda leer de una sentada, que es justo lo
// que hay que poder enseñarle al departamento TIC del centro.
//
// EL INVARIANTE, que es el del modo entero: **el token de aula viaja SOLO en la cabecera
// `X-Aula`**. Nunca en la cookie `access_token`, nunca en `Authorization: Bearer`. El porqué
// completo está escrito en `backend/app/deps/aula.py`; la mitad que toca a este fichero es
// ésta:
//
//   1. Si viajara por donde viaja la sesión, lo único que lo separaría de una sesión de
//      verdad sería una comparación de cadenas dentro del backend. Por una cabecera propia no
//      llega siquiera a donde se lee la sesión.
//   2. Una cookie la manda el navegador en TODAS las peticiones a este dominio, incluidas las
//      que provoque otra página. Una cabecera la pone este fichero a mano, en la petición que
//      la necesita y en ninguna más.
//
// Por eso `leccionDeAula` recibe el token y lo pone él, y no hay ningún interceptor que lo
// añada solo: un interceptor volvería a hacer lo que hace una cookie.
import { api } from "./client";

// El nombre exacto que espera el backend. Se escribe UNA vez para que no haya dos sitios
// donde se pueda escribir mal —el otro extremo hace lo mismo en `app/deps/aula.py`—.
export const CABECERA_AULA = "X-Aula";

// Donde vive el token del alumno. `sessionStorage` Y NO `localStorage`, y es una decisión y no
// una preferencia: la pestaña se cierra y la llave desaparece. En un móvil prestado, en el
// ordenador de la biblioteca del centro o en la tablet que se pasa de mano en mano, una llave
// que sobrevive al día siguiente es un alumno entrando en la clase de otro sin saber que lo
// hace. Y no se guarda NADA más aquí: ni mote, ni respuestas, ni puntuación.
const LLAVE = "aula:sesion";

// ---- El alumno: sin cuenta, sin correo y sin contraseña ----

// «abc-234 » abre igual que «ABC234». El backend normaliza también (`codigos.normaliza`), y
// que lo haga el navegador no sobra: así el campo enseña lo mismo que se manda, y quien copió
// el código con un espacio detrás no ve un error que no ha cometido.
export const normalizaCodigoDeAula = (codigo) =>
  String(codigo || "").toUpperCase().replace(/[^A-Z0-9]/g, "");

// El alfabeto y el largo del código, copiados de `backend/.../codigos.py`. Aquí no deciden
// nada —quien dice si un código abre es el servidor— y sirven para una cosa sola: reconocer
// un código dentro de un texto pegado. Sin las parejas que se confunden (ni O ni 0, ni I ni 1),
// que es lo que hace que «AULA» o «COM» no puedan pasar por uno.
const ALFABETO = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const LARGO = 6;

// EL CODIGO QUE HAY DENTRO DE LO QUE SEA QUE HAYAN PEGADO.
//
// POR QUE EXISTE, y es del 9 de septiembre de 2026: los alumnos NO entran desde el móvil,
// entran desde los Chromebook del centro. O sea que nadie escanea un QR: el profesor pega el
// enlace en el Classroom y treinta personas lo COPIAN. Y lo que llega al campo del código es,
// por ese orden de probabilidad, `https://academialpha.com/aula/ABC234`,
// `academialpha.com/aula/ABC234`, ` abc234 ` o `ABC234`. Las cuatro son la misma intención.
// Contestar «ese código no abre ninguna clase» a quien ha pegado el enlace correcto es la
// diferencia entre treinta alumnos dentro y cinco manos levantadas.
//
// COMO LO SACA: parte el texto por todo lo que no sea letra o número y se queda con el ULTIMO
// trozo que sea exactamente un código posible. El último y no el primero porque en un enlace
// el código va al final, y los trozos de delante —«HTTPS», «ACADEMIALPHA», «COM», «AULA»— no
// pueden colarse: o no miden seis, o llevan letras que el alfabeto no tiene.
//
// SI NO RECONOCE NINGUNO, devuelve el texto normalizado a secas y que conteste el servidor. No
// se inventa un código ni se traga el intento en silencio: el 404 de arriba ya está escrito
// para que lo lea un alumno.
export const codigoDeLoQueSea = (texto) => {
  const trozos = String(texto || "").toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  const bueno = trozos.reverse().find(
    (t) => t.length === LARGO && [...t].every((c) => ALFABETO.includes(c)));
  return bueno || normalizaCodigoDeAula(texto);
};

// El código de la pizarra a cambio de `{token, clase, hasta, lecciones}`. No manda nada de
// nadie: ni correo, ni nombre, ni identificador del aparato. No hay nada que mandar.
//
// Pasa por `codigoDeLoQueSea` y no por `normalizaCodigoDeAula`: la puerta está en un sitio
// solo, así que da igual si lo que llega es un código, un enlace entero o lo que había en el
// portapapeles. Para un código a secas las dos hacen lo mismo.
export const entraEnElAula = async (codigo) => {
  const { data } = await api.post("/aula/entrar", { codigo: codigoDeLoQueSea(codigo) });
  return data;
};

// La lección que el profesor abrió para esta hora. MISMO endpoint que usa el alumno con
// cuenta: lo que cambia es la llave, no el contenido. El token va aquí y en ningún otro sitio.
export const leccionDeAula = async (lessonId, token) => {
  const { data } = await api.get(`/content/lessons/${lessonId}`, {
    headers: { [CABECERA_AULA]: token },
  });
  return data;
};

// ---- La llave, mientras dure la pestaña ----

export const guardaSesionDeAula = (sesion) => {
  try {
    sessionStorage.setItem(LLAVE, JSON.stringify(sesion));
  } catch {
    // Navegación privada, almacenamiento lleno o bloqueado por el centro. No se avisa: la
    // clase sigue funcionando en memoria y lo único que se pierde es sobrevivir a un
    // recargado. Reventar aquí sería dejar fuera al alumno por un permiso del navegador.
  }
};

export const olvidaSesionDeAula = () => {
  try {
    sessionStorage.removeItem(LLAVE);
  } catch {
    // Ver `guardaSesionDeAula`.
  }
};

// Devuelve la sesión guardada, o `null`. UNA SESION CADUCADA ES `null` Y SE BORRA AL LEERLA:
// si no, al volver a abrir la pestaña se pintaría la lista de lecciones de una clase que
// terminó hace dos horas y el alumno descubriría que no abre ninguna pulsándolas una a una.
export const leeSesionDeAula = () => {
  try {
    const crudo = sessionStorage.getItem(LLAVE);
    if (!crudo) return null;
    const sesion = JSON.parse(crudo);
    if (!sesion?.token) return null;
    const fin = Date.parse(sesion.hasta || "");
    if (Number.isNaN(fin) || fin <= Date.now()) {
      olvidaSesionDeAula();
      return null;
    }
    return sesion;
  } catch {
    return null;
  }
};

// ---- El profesor: abrir, mirar y cerrar ----
//
// TRES LLAMADAS Y NO UNA CON UN INTERRUPTOR, igual que el acceso promocional de esta misma
// clase: abrir y cerrar son acciones distintas con consecuencias distintas, y con un
// `PUT {abierta}` dejar a treinta alumnos fuera a mitad de clase sería un carácter de
// diferencia respecto a dejarlos entrar.

export const estadoSesionAula = async (communityId) => {
  const { data } = await api.get(`/admin/communities/${communityId}/sesion-aula`);
  return data;
};

// `minutos` puede no ir: el servidor pone una hora, que es lo que dura una clase, y recorta
// lo que se le pida al rango que aguanta (5 a 240).
export const abreSesionAula = async (communityId, lessonIds, minutos) => {
  const { data } = await api.post(`/admin/communities/${communityId}/sesion-aula`, {
    lesson_ids: lessonIds,
    minutos,
  });
  return data;
};

export const cierraSesionAula = async (communityId) => {
  const { data } = await api.delete(`/admin/communities/${communityId}/sesion-aula`);
  return data;
};
